import { STATE_OPTIONS } from "@/utils/states";

export const STATE_OPTS = STATE_OPTIONS.map(([value, label, hint]) => ({ value, label, hint }));

export const TITLES = [
  "Owner", "Co-Owner", "President", "Vice President", "Senior Vice President",
  "Secretary", "Corporate Secretary", "Secretary/Treasurer", "CEO", "Controller",
  "Financial Officer", "General Manager", "General Partner", "Limited Partner",
  "Manager", "Member", "Director", "Executive Director",
];

export const OWNERSHIP_TYPES = [
  "Sole Proprietorship", "Partnership", "General Partnership", "Limited Partnership",
  "Limited Liability Partnership", "LLC", "Corporation - Private", "Corporation - Public",
  "Professional Association", "Non-Profit", "Political Organization", "Government",
  "Trust", "Financial Institution", "Other",
];

const LEGACY_OWNERSHIP_TYPES: Record<string, string> = {
  "sole proprietor": "Sole Proprietorship",
  "private corporation": "Corporation - Private",
  "public corporation": "Corporation - Public",
  "nonprofit": "Non-Profit",
};

const LEGACY_TITLES: Record<string, string> = {
  "cfo": "Financial Officer",
  "treasurer": "Financial Officer",
  "partner": "General Partner",
};

export function canonicalOwnershipType(value: string): string {
  return LEGACY_OWNERSHIP_TYPES[value.trim().toLowerCase()] ?? value;
}

export function canonicalOwnerTitle(value: string): string {
  return LEGACY_TITLES[value.trim().toLowerCase()] ?? value;
}

export const OPT = {
  cbNotify: ["Fax", "Email"],
  addr3: ["Legal", "DBA", "Fax", "Email"],
  addr4: ["Legal", "DBA", "Electronic"],
  ownership: OWNERSHIP_TYPES,
  streetDirection: [
    "North", "South", "East", "West", "Northeast", "Northwest", "Southeast", "Southwest",
  ],
  streetType: [
    "Street", "Avenue", "Boulevard", "Drive", "Lane", "Road", "Court", "Circle",
    "Place", "Way", "Terrace", "Trail", "Parkway", "Highway",
  ],
  contactTitle: [
    "CEO", "Co-Owner", "Controller", "Corporate Secretary", "Director", "Executive Director",
    "First Officer", "General Manager", "General Partner", "IRS / Tax Contact", "Limited Partner",
    "Manager", "Member", "Owner", "President", "Secretary", "Secretary/Treasurer",
    "Senior Vice President", "Vice President", "Other",
  ],
  leadSource: [
    { value: "TF", label: "TF — TransFirst" },
    { value: "STW", label: "STW — Sales/STW" },
    { value: "PPSTW", label: "PPSTW" },
    { value: "PPTF", label: "PPTF" },
  ],
  taxIdType: ["EIN", "SSN", "ITIN"],
  businessType: [
    "Retail", "Retail with Tips", "Restaurant", "MOTO", "Internet", "Lodging",
    "Supermarket", "Utility", "Pharmacy", "Financial Institution", "Business to Business",
  ],
  cardTypes: [
    { value: "AllCredit", label: "All Credit Cards" },
    { value: "AllCreditPlusPinDebit", label: "All Credit + PIN Debit" },
    { value: "PinDebitOnly", label: "PIN Debit Only" },
  ],
  procMethod: [
    { value: "EDCRetail", label: "Retail (card present)" },
    { value: "EDCCNP", label: "Card not present" },
    { value: "TouchTone", label: "Touch-tone" },
    { value: "Paper", label: "Paper" },
    { value: "Other", label: "Other" },
  ],
  trainedBy: ["TransFirst", "Agent"],
  shipTo: [
    { value: "NA", label: "N/A" },
    { value: "DBA", label: "DBA" },
    { value: "Legal", label: "Legal" },
    { value: "Chargeback", label: "Chargeback" },
    { value: "Mailing", label: "Mailing" },
    { value: "Shipping", label: "Shipping" },
    { value: "Statement", label: "Statement" },
    { value: "Deployment", label: "Deployment" },
    { value: "IRS", label: "IRS" },
    { value: "Agent", label: "Agent" },
    { value: "Other", label: "Other" },
  ],
  checkSvc: [
    { value: "StandardVerification", label: "Standard — Verification" },
    { value: "StandardGuarantee", label: "Standard — Guarantee" },
    { value: "PremiumVerification", label: "Premium — Verification" },
    { value: "PremiumGuarantee", label: "Premium — Guarantee" },
  ],
  patriotSection: [
    { value: "I", label: "I — Government entity" },
    { value: "II", label: "II — Business" },
    { value: "III", label: "III — Individual" },
  ],
  patriotGovtDoc: [
    { value: "GovtArticles", label: "Government Entity Articles of Incorporation" },
    { value: "GovtTaxLetter", label: "Government Entity Tax Determination Letter" },
    { value: "GovtThirdParty", label: "Government Entity Third-Party Verification" },
  ],
  patriotBusinessDoc: [
    { value: "BusinessLicense", label: "Government Issued Business License" },
    { value: "TaxReturn", label: "Tax Return" },
    { value: "ArticlesOfIncorporation", label: "Articles of Incorporation" },
    { value: "FinancialStatements", label: "Business Financial Statements" },
  ],
  patriotIndividualDoc: [
    { value: "StateID", label: "State ID" },
    { value: "Passport", label: "Passport" },
    { value: "MilitaryID", label: "Military ID" },
  ],
  siteOption: [
    { value: "A", label: "A — On-site visit by sales rep" },
    { value: "B", label: "B — No site inspection (license attached)" },
    { value: "C", label: "C — Sales partner validated" },
  ],
  processingType: ["Retail", "MOTO"],
  pricingPlan: ["Interchange Plus", "Differential", "Surcharge"],
  bankPurpose: ["Deposit", "Withdraw", "Both", "ACH"],
  accountType: [
    { value: "Checking", label: "Checking" }, { value: "Savings", label: "Savings" },
    { value: "BankGL", label: "Bank GL" },
  ],
  posIndustry: [
    { value: "RETL", label: "Retail" },
    { value: "eCOM", label: "eCommerce" },
    { value: "MOPO", label: "Mail or Phone Order" },
  ],
  batchClose: ["AUTO", "MANUAL"],
  locationType: ["Single", "Multiple"],
  shipDays: [
    { value: "0-2", label: "0-2 days" },
    { value: "3-30", label: "3-30 days" },
    { value: "31-60", label: "31-60 days" },
    { value: "61-90", label: "61-90 days" },
    { value: "Over90", label: "Over 90 days" },
  ],
  billTo: ["Merchant", "Agent", "Global Payments", "N/A"],
  equipmentCode: [
    { value: "MO", label: "MO — Merchant Owned" },
    { value: "PN", label: "PN — Purchase New" },
    { value: "PRF", label: "PRF — Purchased Refurbished" },
    { value: "RTL", label: "RTL — Rental Program" },
    { value: "STR", label: "STR — Short Term Rental" },
    { value: "FUA", label: "FUA — Free Use Addendum" },
  ],
};
